import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import useTerraDraw, {
  polygonFeatureToPath,
  circleFeatureToCircle,
} from "../../../../components/Maps/useTerraDraw";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 37.7749,
  lng: -122.4194,
};

const REACT_APP_MAP_TOKEN = process.env.REACT_APP_MAP_TOKEN;
// "drawing" removed from the Maps JS API in v3.65 (May 2026); drawing is done via Terra Draw
const libraries = ["places", "marker", "core"];

const MapDisplay = forwardRef(
  (
    {
      mode,
      shapeType,
      shapeName,
      layoutData,
      setLayoutData,
      onShapeDrawn,
      onShapeEditInitiated,
      onShapeEditConfirmed,
    },
    ref
  ) => {
    const mapRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const modeRef = useRef(mode);
    const shapeTypeRef = useRef(shapeType);
    const shapeNameRef = useRef(shapeName);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedShape, setSelectedShape] = useState(null);

    useEffect(() => {
      modeRef.current = mode;
    }, [mode]);
    useEffect(() => {
      shapeTypeRef.current = shapeType;
    }, [shapeType]);
    useEffect(() => {
      shapeNameRef.current = shapeName;
    }, [shapeName]);

    const { isLoaded, loadError } = useJsApiLoader({
      googleMapsApiKey: REACT_APP_MAP_TOKEN,
      libraries,
    });

    // Build the shape info object consumers expect from a Terra Draw GeoJSON feature
    const featureToShapeInfo = (feature) => {
      const type = feature?.properties?.shapeType ?? shapeTypeRef.current;
      const name = feature?.properties?.shapeName ?? "";
      const base = { id: feature.id, type, name };
      if (feature?.properties?.mode === "circle") {
        const { center: c, radius } = circleFeatureToCircle(feature);
        return { ...base, center: c, radius };
      }
      return { ...base, paths: polygonFeatureToPath(feature) };
    };

    const { draw, ready, setMode: setDrawMode } = useTerraDraw(mapInstance, {
      enabled: true,
      onFinish: (feature, drawInstance) => {
        const type = shapeTypeRef.current;
        const name = shapeNameRef.current;

        // Tag the feature so later select (edit/delete) knows what it is
        try {
          drawInstance.updateFeatureProperties(feature.id, {
            shapeType: type,
            shapeName: name,
          });
        } catch (_) {
          /* non-fatal */
        }

        let shapeData;
        if (feature?.properties?.mode === "circle") {
          const { center: c, radius } = circleFeatureToCircle(feature);
          shapeData = { id: feature.id, type, name, center: c, radius };
        } else {
          shapeData = {
            id: feature.id,
            type,
            name,
            paths: polygonFeatureToPath(feature),
          };
        }
        onShapeDrawn(shapeData);
      },
      onSelect: (feature) => {
        if (modeRef.current === "edit") {
          if (onShapeEditInitiated) {
            onShapeEditInitiated(featureToShapeInfo(feature));
          }
        } else if (modeRef.current === "delete") {
          setSelectedShape({
            id: feature.id,
            type: feature?.properties?.shapeType ?? shapeTypeRef.current,
          });
          setDeleteModalOpen(true);
        }
      },
      polygonStyles: {
        fillColor: shapeType === "geofence" ? "#FFFF00" : "#FF00FF",
        fillOpacity: 0.5,
        outlineColor: shapeType === "geofence" ? "#FFFF00" : "#FF00FF",
        outlineWidth: 2,
      },
      circleStyles: {
        fillColor: "#0000FF",
        fillOpacity: 0.5,
        outlineColor: "#0000FF",
        outlineWidth: 2,
      },
      selectFlags: {
        polygon: { feature: { coordinates: { editable: true } } },
        circle: { feature: {} },
      },
    });

    // Keep the Terra Draw mode in sync with the app's draw/edit/delete mode
    useEffect(() => {
      if (!ready) return;
      if (mode === "draw") {
        // Polygon color depends on the shape type (geofence yellow, outline pink)
        try {
          const color = shapeType === "geofence" ? "#FFFF00" : "#FF00FF";
          draw.current?.updateModeOptions("polygon", {
            styles: {
              fillColor: color,
              fillOpacity: 0.5,
              outlineColor: color,
              outlineWidth: 2,
            },
          });
        } catch (_) {
          /* styling is non-critical */
        }
        setDrawMode(shapeType === "zone" ? "circle" : "polygon");
      } else if (mode === "edit" || mode === "delete") {
        setDrawMode("select");
      } else {
        setDrawMode("static");
      }
    }, [ready, mode, shapeType, setDrawMode, draw]);

    useImperativeHandle(ref, () => ({
      cancelDrawMode: () => {
        setDrawMode("static");
      },
    }));

    useEffect(() => {
      if (!onShapeEditConfirmed) return;

      const handler = ({ id, name }) => {
        // Keep the feature's name in sync so future edits show the new name
        try {
          draw.current?.updateFeatureProperties(id, { shapeName: name });
        } catch (_) {
          /* non-fatal */
        }
        setLayoutData((prev) => {
          const updated = JSON.parse(JSON.stringify(prev));
          const keys = Object.keys(updated);

          for (let key of keys) {
            const index = updated[key].findIndex((s) => s.id === id);
            if (index !== -1) {
              updated[key][index].name = name;
              break;
            }
          }

          return updated;
        });
      };

      onShapeEditConfirmed(handler);
    }, [onShapeEditConfirmed, draw, setLayoutData]);

    const handleDeleteConfirmed = () => {
      if (selectedShape) {
        try {
          draw.current?.removeFeatures([selectedShape.id]);
        } catch (_) {
          /* feature may already be gone */
        }
        setLayoutData((prev) => {
          const newLayout = JSON.parse(JSON.stringify(prev));
          const type = selectedShape.type;
          const key = type === "outline" ? "facilityOutlines" : type + "s";
          if (Array.isArray(newLayout[key])) {
            newLayout[key] = newLayout[key].filter(
              (s) => s.id !== selectedShape.id
            );
          }
          return newLayout;
        });
      }
      setDeleteModalOpen(false);
      setSelectedShape(null);
    };

    const handleDeleteCancelled = () => {
      setDeleteModalOpen(false);
      setSelectedShape(null);
    };

    if (loadError) return <div>Error loading map</div>;
    if (!isLoaded) return <div>Loading map...</div>;

    return (
      <>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}
          onLoad={(map) => {
            mapRef.current = map;
            setMapInstance(map);
          }}
          onUnmount={() => {
            mapRef.current = null;
            setMapInstance(null);
          }}
        />

        <Dialog open={deleteModalOpen} onClose={handleDeleteCancelled}>
          <DialogTitle>Delete Shape?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this shape? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancelled}>Cancel</Button>
            <Button onClick={handleDeleteConfirmed} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default MapDisplay;
